import React, { useState } from 'react';
import {
  Card,
  Box,
  Divider
} from '@mui/material';
import Image from 'next/image';
import { type Task, type SubTask } from '../../../stores/TaskStore';
import MyEditor from './mdEditor/MyEditor';
import { TaskHeader } from './TaskSubPanel/TaskHeader';
import { SubTaskList } from './TaskSubPanel/SubTaskList';
import { AddSubTask } from './TaskSubPanel/AddSubTask';
import EditNoteIcon from '@mui/icons-material/EditNote';

interface TaskSubPanelProps {
  task: Task | undefined;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
}

export const TaskSubPanel: React.FC<TaskSubPanelProps> = ({
  task,
  onUpdateTask
}) => {
  const [newSubTaskContent, setNewSubTaskContent] = useState('');
  const [isAddingSubTask, setIsAddingSubTask] = useState(false);
  const [editingSubTaskId, setEditingSubTaskId] = useState<string | null>(null);
  const [editingSubTaskContent, setEditingSubTaskContent] = useState('');

  if (!task) {
    return (
      <Card
        sx={{
          height: '600px',
          display: 'flex',
          flexDirection: 'column', // 垂直排列
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          p: 3,
          gap: 2, // 增加图标和文字的间距
          color: 'text.secondary' // 使用次要文字颜色
        }}
        className='rounded-2xl'
      >
        <Image
          src="/task-list-f-svgrepo-com.svg"
          alt="No task selected"
          width={64}
          height={64}
          style={{ opacity: 0.5 }}
        />
        <Box sx={{ fontSize: '1.1rem' }}>Select a task to see details</Box>
      </Card>
    );
  }

  const handleEditSubTask = (subTask: SubTask) => {
    setEditingSubTaskId(subTask.id);
    setEditingSubTaskContent(subTask.content);
  };

  const handleUpdateSubTaskContent = () => {
    if (!editingSubTaskId) return;

    const updatedSubTasks = (task.subTasks || []).map(subTask =>
      subTask.id === editingSubTaskId
        ? { ...subTask, content: editingSubTaskContent }
        : subTask
    );
    onUpdateTask(task.id, { subTasks: updatedSubTasks });

    // Reset editing state
    setEditingSubTaskId(null);
    setEditingSubTaskContent('');
  };

  const handleAddSubTask = () => {
    if (newSubTaskContent.trim()) {
      const newSubTask: SubTask = {
        id: `subtask_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        content: newSubTaskContent.trim(),
        completed: false,
        createdAt: new Date()
      };

      const updatedSubTasks = [...(task.subTasks || []), newSubTask];
      onUpdateTask(task.id, { subTasks: updatedSubTasks });
      setNewSubTaskContent('');
      setIsAddingSubTask(false); // 添加完成后回到初始状态
    }
  };

  const handleBlur = () => {
    if (newSubTaskContent.trim()) {
      handleAddSubTask();
    } else {
      setIsAddingSubTask(false);
      setNewSubTaskContent('');
    }
  };

  const handleToggleSubTask = (subTaskId: string) => {
    const updatedSubTasks = (task.subTasks || []).map(subTask =>
      subTask.id === subTaskId
        ? { ...subTask, completed: !subTask.completed }
        : subTask
    );
    onUpdateTask(task.id, { subTasks: updatedSubTasks });
  };

  const handleDeleteSubTask = (subTaskId: string) => {
    const updatedSubTasks = (task.subTasks || []).filter(subTask => subTask.id !== subTaskId);
    onUpdateTask(task.id, { subTasks: updatedSubTasks });
  };

  // Accept only ISO string (or null/undefined) and return a localized date/time string.
  const formatDateTime = (date?: string | Date) => {
    if (!date) return '';

    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';

    return d.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card
      sx={{
        height: '600px', // 设置固定高度，与 TaskManager 保持一致
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        transition: 'all 0.3s ease',
      }}
      className='rounded-2xl p-6'
    >
      <TaskHeader task={task} formatDateTime={formatDateTime} />

      {/* SubTask部分 */}
      <Box sx={{
        flex: 1, // 占据剩余空间
        display: 'flex',
        flexDirection: 'column',
        px: 1,
        overflowY: 'auto', // 允许垂直滚动
        minHeight: 0,
      }}>
        <SubTaskList
          task={task}
          editingSubTaskId={editingSubTaskId}
          editingSubTaskContent={editingSubTaskContent}
          setEditingSubTaskContent={setEditingSubTaskContent}
          handleToggleSubTask={handleToggleSubTask}
          handleDeleteSubTask={handleDeleteSubTask}
          handleEditSubTask={handleEditSubTask}
          handleUpdateSubTaskContent={handleUpdateSubTaskContent}
        />
        <AddSubTask
          isAddingSubTask={isAddingSubTask}
          setIsAddingSubTask={setIsAddingSubTask}
          newSubTaskContent={newSubTaskContent}
          setNewSubTaskContent={setNewSubTaskContent}
          handleAddSubTask={handleAddSubTask}
          handleBlur={handleBlur}
        />
      </Box>

      {/* 编辑器区域 */}
      <Box sx={{
        flexShrink: 0, // 防止编辑器区域被压缩
        height: '250px', // 稍微增加编辑器的高度
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center'}}>
          <EditNoteIcon sx={{ color: 'var(--color-primary)'}} />
          <Divider sx={{ flexGrow: 1, borderColor: 'var(--color-primary)' }} />
        </Box>
        <Box sx={{ flex: 1, position: 'relative' }}>
          <MyEditor id={task.id} />
        </Box>
      </Box>
    </Card>
  );
};
